export interface BlogPost {
  slug: string;
  title: string;
  excerpt: string;
  date: string;
  readTime: string;
  category: string;
  // When set, the detail page renders a real article. Without it, the post shows a
  // "coming soon" placeholder — so we never present an empty page as finished writing.
  content?: string;
}

export const BLOG_POSTS: BlogPost[] = [
  {
    slug: "one-dashboard-for-all-your-marketing",
    title: "Run All Your Marketing From One Dashboard",
    excerpt:
      "Email here, ads in three different managers, social somewhere else. Here's the case for pulling every channel — and every number — into one view.",
    date: "June 9, 2026",
    readTime: "5 min read",
    category: "Strategy",
    content: `
      <p>Most founders don't have a marketing problem. They have a <em>fragmentation</em> problem. Email lives in one tool, ads in three different managers, social in another, and the analytics are scattered across all of them. By the time you've logged into everything, the day is gone — and you still can't answer a simple question: <strong>is any of this working?</strong></p>
      <h2>The hidden cost of scattered tools</h2>
      <p>Every disconnected tool is a tab you have to open, a login you have to remember, and a number you have to copy into a spreadsheet to make sense of. The average early-stage team juggles six or more. The real cost isn't the subscriptions — it's that no one can see the whole picture, so decisions get made on gut feel instead of evidence.</p>
      <h2>What "one dashboard" actually means</h2>
      <p>A real command center does two things. First, it <strong>pulls every channel into one view</strong> — ad spend, email performance, social engagement, web traffic, and pipeline, side by side. Second, it <strong>makes sense of it for you</strong>: which channel is pulling its weight, where your money is leaking, and what to do next.</p>
      <p>The point isn't a prettier chart. It's that when your cost-per-click on one channel triples, you see it next to the others and can move budget the same afternoon — without exporting anything.</p>
      <h2>Honesty over vanity metrics</h2>
      <p>A dashboard is only useful if you can trust it. That means showing real numbers, flagging clearly when a channel hasn't synced, and never dressing up an empty account as a success. A number you can't trust is worse than no number at all.</p>
      <p>If you're tired of marketing-by-browser-tabs, that single, trustworthy view is the foundation everything else builds on.</p>
    `,
  },
  {
    slug: "what-an-ai-marketing-agency-actually-does",
    title: "What an AI Marketing Agency Actually Does For You",
    excerpt:
      "Not magic, not a chatbot. A team of specialists that drafts your campaigns and waits for your approval before anything goes live.",
    date: "June 2, 2026",
    readTime: "6 min read",
    category: "AI Marketing",
    content: `
      <p>"AI marketing agency" sounds like a buzzword, so let's be specific about what it actually means — and what it doesn't.</p>
      <h2>It's a team, not a chatbot</h2>
      <p>Instead of one general-purpose assistant, you get a set of specialists — a strategist, a copywriter, a social manager, a media planner, and more. You brief them the way you'd brief a real agency: tell them your business, your audience, and what you're trying to do.</p>
      <h2>They draft. You approve. Then it ships.</h2>
      <p>This is the part that matters most. The team doesn't fire campaigns into the world on your behalf. They <strong>draft</strong> the emails, the posts, the ad creative, and the landing pages — and everything lands in an approval queue. Nothing goes live until you say so. You're the CMO; they're the team.</p>
      <p>That approval gate is the difference between a tool that's useful and one that's dangerous. AI is good at producing a lot of work quickly. A human deciding what's actually good enough to publish is what keeps the quality — and your brand — intact.</p>
      <h2>What it's good at (and what it isn't)</h2>
      <p>It's genuinely good at the first 80%: turning a one-line brief into a full multi-channel campaign in minutes, holding a consistent voice, and handling the repetitive production work that eats your week. It is <strong>not</strong> a replacement for your judgment about what your customers actually need. The best results come from treating it like a capable team that still reports to you.</p>
      <p>Used that way, it gives a solo founder the output of a marketing department — without the headcount.</p>
    `,
  },
  {
    slug: "replace-marketing-team-with-ai",
    title: "How to Replace Your Marketing Team with AI",
    excerpt:
      "Discover how AI marketing teams are helping startups and small businesses compete without the enterprise-level budget.",
    date: "March 10, 2026",
    readTime: "8 min read",
    category: "AI Marketing",
  },
  {
    slug: "complete-guide-marketing-automation-startups",
    title: "The Complete Guide to Marketing Automation for Startups",
    excerpt:
      "From email drip campaigns to social media scheduling, learn how to automate your marketing funnel and save time every week.",
    date: "March 5, 2026",
    readTime: "12 min read",
    category: "Automation",
  },
  {
    slug: "5-ai-marketing-tools-2026",
    title: "5 AI Marketing Tools Every Startup Needs in 2026",
    excerpt:
      "The AI marketing landscape is evolving fast. Here are the tools that are actually worth your time and money this year.",
    date: "February 28, 2026",
    readTime: "6 min read",
    category: "Tools",
  },
];

export function getPost(slug: string): BlogPost | undefined {
  return BLOG_POSTS.find((p) => p.slug === slug);
}
